import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import {
  createJobNote,
  jobNotesQueryKey,
  listJobNotes,
} from '@/lib/api/jobs';

import { formatDate } from './helpers';
import { jobNoteSchema, type JobNoteInput } from './schema';

interface Props {
  businessId: string;
  jobId: string;
}

// Notes list + compose box for the job detail modal. Self-contained: owns its
// own query + create mutation; the global MutationCache refetches + toasts.
export function JobNotesSection({ businessId, jobId }: Props) {
  const { t } = useLanguage();

  const notesQuery = useQuery({
    queryKey: jobNotesQueryKey(businessId, jobId),
    queryFn: () => listJobNotes(businessId, jobId),
  });

  const form = useForm<JobNoteInput>({
    defaultValues: { body: '' },
    resolver: zodResolver(jobNoteSchema),
  });

  const addMutation = useMutation({
    mutationFn: (data: JobNoteInput) =>
      createJobNote(businessId, jobId, { body: data.body }),
    meta: { successMessage: t.jobNoteAdded },
    onSuccess: () => form.reset({ body: '' }),
  });

  const notes = notesQuery.data ?? [];

  return (
    <div className="px-6 py-5 border-t flex flex-col gap-3">
      <h3 className="text-sm font-semibold">{t.jobNotesHeading}</h3>

      {notesQuery.isPending ? (
        <p className="text-sm text-muted-foreground">{t.loading}</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.jobNoNotes}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {notes.map((note) => (
            <li key={note.id} className="text-sm" data-testid={`job-note-${note.id}`}>
              <p className="whitespace-pre-wrap">{note.body}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {note.author_full_name} · {formatDate(note.created_at.slice(0, 10))}
              </p>
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={form.handleSubmit((data) => addMutation.mutate(data))}
        className="flex flex-col gap-2 pt-1"
      >
        <Controller
          control={form.control}
          name="body"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1.5">
              <textarea
                {...field}
                rows={2}
                placeholder={t.jobNotePlaceholder}
                aria-label={t.jobNoteBody}
                aria-invalid={fieldState.invalid}
                data-testid="job-note-input"
                className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
              {fieldState.error && (
                <p className="text-xs text-destructive">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />
        <Button
          type="submit"
          size="sm"
          className="self-start"
          disabled={addMutation.isPending}
          data-testid="job-note-add"
        >
          {t.jobAddNote}
        </Button>
      </form>
    </div>
  );
}
